import {useResizableChartWidth} from '@widgets/common-d3-chart'
import {
  StockDevelopmentChartConfigProvider,
  STOCK_DEVELOPMENT_CHART_DEFAULT_HEIGHT,
  StockDevelopmentChartContent,
  StockDevelopmentChartContentProps,
  useStockDevelopmentChartConfigData
} from '@widgets/stock-card'
import * as d3 from 'd3'
import {useCallback} from 'react'

import {IncomingMaterial} from '../../types'

import {IncomingMaterialIndicator} from './incomingMaterials/IncomingMaterialIndicator'

type StaticStockDevelopmentChartProps = Omit<
  StockDevelopmentChartContentProps,
  'chartRef' | 'width' | 'renderIndicators'
> & {
  timezoneId: string
  height?: number
  incomingMaterialHourly?: Record<string, IncomingMaterial[]>
}

export const StaticStockDevelopmentChart = (props: StaticStockDevelopmentChartProps) => {
  const [chartRef, width] = useResizableChartWidth()
  const {incomingMaterialHourly, ...contentProps} = props

  const data = useStockDevelopmentChartConfigData({
    stockData: props.stockData,
    listOfDays: props.listOfDays,
    timezoneId: props.timezoneId,
    minTargetLevels: props.minTargetLevels,
    storageCapacity: props.storageCapacity,
    height: props.height || STOCK_DEVELOPMENT_CHART_DEFAULT_HEIGHT,
    width: width
  })

  const renderIndicators = useCallback(
    ({
      xScale,
      yScale,
      stockLevelHourly
    }: {
      xScale: d3.ScaleTime<number, number, never>
      yScale: d3.ScaleLinear<number, number, never>
      stockLevelHourly: Record<string, number>
    }) => {
      if (!incomingMaterialHourly) return null

      return Object.entries(incomingMaterialHourly).flatMap(([datetime, materials]) =>
        materials.map((material, index) => {
          const x = xScale(material.datetime.toDate())
          const stockLevel = stockLevelHourly[material.datetime.toISOString()] ?? 0
          const cumulativeValue = materials.slice(0, index).reduce((acc, m) => acc + m.value, 0)
          const y = yScale(stockLevel + cumulativeValue)
          return (
            <IncomingMaterialIndicator
              key={`${datetime}-${index}`}
              x={x}
              y={y}
              incomingMaterial={material}
            />
          )
        })
      )
    },
    [incomingMaterialHourly]
  )

  return (
    <StockDevelopmentChartConfigProvider {...data}>
      <StockDevelopmentChartContent
        {...contentProps}
        chartRef={chartRef}
        width={width}
        renderIndicators={incomingMaterialHourly ? renderIndicators : undefined}
      />
    </StockDevelopmentChartConfigProvider>
  )
}
